import { useState, useEffect, useMemo } from 'react';
import { useSeller } from '@/components/layout/DashboardLayout';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Package, AlertTriangle, TrendingDown, ShieldAlert } from 'lucide-react';

export default function AsinRiskPage() {
  const { hasSelection } = useProfile();
  const { currentSellerId, syncVersion, syncing } = useSeller();
  const [asins, setAsins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentSellerId) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        const res = await sellersAPI.getAsinRisks(currentSellerId);
        setAsins(res.data);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    load();
  }, [currentSellerId, syncVersion]);

  const sortedAsins = useMemo(
    () => [...asins].sort((a, b) => Number(b.risk_score) - Number(a.risk_score)),
    [asins]
  );

  if (loading || (hasSelection && syncing)) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  const atRisk = asins.filter(a => Number(a.risk_score) > 50);
  const suppressed = asins.filter(a => a.listing_status === 'suppressed');
  const lowerSeverity = asins.filter(a => Number(a.risk_score) <= 25);

  return (
    <div className="space-y-6" data-testid="asin-risk-page">
      <div>
        <h1 className="text-xl font-semibold">ASINs at Risk</h1>
        <p className="text-sm text-muted-foreground">
          ASINs with at least one risk flag — title, category, BSR, and scores when the API provides them
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border/80">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(188,100%,55%)]/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-[hsl(188,100%,55%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{asins.length}</p>
              <p className="text-xs text-muted-foreground">Flagged ASINs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(0,84%,60%)]/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-[hsl(0,84%,60%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{atRisk.length}</p>
              <p className="text-xs text-muted-foreground">High risk score (&gt;50)</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(24,94%,56%)]/10 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-[hsl(24,94%,56%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{suppressed.length}</p>
              <p className="text-xs text-muted-foreground">Suppressed</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(142,72%,45%)]/10 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-[hsl(142,72%,45%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{lowerSeverity.length}</p>
              <p className="text-xs text-muted-foreground">Lower score (≤25)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border/80" data-testid="asin-risk-table">
        <CardContent className="p-0">
          <div className="rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="text-xs font-semibold whitespace-nowrap">ASIN</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">Product Title</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">Category</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">BSR</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">Risk Score</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">Status</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">Risk Flags</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">Return Rate</TableHead>
                  <TableHead className="text-xs font-semibold whitespace-nowrap">Buy Box</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAsins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-sm text-muted-foreground py-8 text-center">
                      No ASINs with risk flags for this account.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedAsins.map((asin) => {
                    const score = Number(asin.risk_score);
                    return (
                      <TableRow key={asin.id ?? asin.asin} className="hover:bg-muted/20">
                        <TableCell className="text-sm font-mono text-[hsl(188,100%,55%)] whitespace-nowrap">
                          {asin.asin}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{asin.title ?? '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[140px] truncate">
                          {asin.category ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums text-muted-foreground whitespace-nowrap">
                          {asin.bsr != null && asin.bsr !== '' ? `#${Number(asin.bsr).toLocaleString()}` : '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center gap-2 min-w-[5.5rem]">
                            <div className="w-16 h-1.5 bg-muted/40 rounded-full overflow-hidden shrink-0">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${Math.min(100, score)}%`,
                                  background:
                                    score > 50 ? 'hsl(0,84%,60%)' : score > 25 ? 'hsl(45,93%,58%)' : 'hsl(142,72%,45%)',
                                }}
                              />
                            </div>
                            <span className="text-xs tabular-nums">{Number.isFinite(score) ? score : '—'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-[10px]',
                              asin.listing_status === 'suppressed'
                                ? 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30'
                                : asin.listing_status === 'warning'
                                  ? 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30'
                                  : 'bg-[hsl(142,72%,45%)]/15 text-[hsl(142,72%,45%)] border-[hsl(142,72%,45%)]/30'
                            )}
                          >
                            {asin.listing_status ?? 'active'}
                          </Badge>
                        </TableCell>
                        <TableCell className="min-w-[120px]">
                          <div className="flex flex-wrap gap-1">
                            {(asin.risk_flags || [])
                              .filter((f) => f && f !== 'None')
                              .map((flag, i) => (
                                <Badge key={i} variant="outline" className="text-[9px] border-border/60">
                                  {flag}
                                </Badge>
                              ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums text-muted-foreground whitespace-nowrap">
                          {asin.return_rate != null && asin.return_rate !== ''
                            ? `${Number(asin.return_rate).toFixed(1)}%`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums text-muted-foreground whitespace-nowrap">
                          {asin.buy_box_pct != null && asin.buy_box_pct !== ''
                            ? `${Number(asin.buy_box_pct).toFixed(0)}%`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
